import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { FirebaseContext } from '../contexts/FirebaseContext';
import { auth } from '../services/firebase/core';
import { setInitialized, login, logout, setLoading, convertToUser } from '../store/slices/authSlice';
import { setCurrentProfile } from '../store/slices/userSlice';
import { onAuthStateChanged, User } from 'firebase/auth';
import { getProfileByUid } from '../services/firebase/profile.service';
import { getUserBeltPromotions } from '../services/firebase/belt-progression.service';
import { simpleIAPService } from '../services/iap-simple.service';
import { attachPurchaseListener, checkSubscriptionStatus } from '../store/slices/premiumSlice';
import { initializePushNotificationsNative, cleanupPushNotificationsNative, savePendingPushToken } from '../services/push-notifications-native.service';
import { healthSyncService } from '../services/health/health-sync.service';
import logger from '../utils/logger';

export const FirebaseProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const dispatch = useDispatch();
  const [isInitialized, setIsInitialized] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<User | null>(null);

  useEffect(() => {
    let unsubscribe: (() => void) | null = null;

    const completeInitialization = () => {
      dispatch(setInitialized());
      setIsInitialized(true);
      setIsLoading(false);
      dispatch(setLoading(false));
    };

    const initializeAuth = async () => {
      try {
        logger.info('🔐 Setting up auth listener (original app pattern)');
        setIsLoading(true);
        dispatch(setLoading(true));

        // Single auth listener - matches original app pattern
        unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
          logger.info('🔐 Auth state changed:', firebaseUser?.uid || 'null');

          if (firebaseUser) {
            // CRITICAL FIX: Properly await database connectivity before loading profile
            // This was causing race conditions where profile load failed silently
            try {
              const { ensureDatabaseOnline } = await import('../services/firebase/database-connection');
              await ensureDatabaseOnline();
              logger.info('✅ Database connection established');
            } catch (dbError) {
              logger.error('❌ Could not establish database connection:', dbError);
              // Continue anyway but log the error - this might cause profile load to fail
            }

            // Profile loading with retry logic (matching original app tiered loading)
            let profileData = null;
            let profileLoadError = null;
            const maxRetries = 3;

            for (let attempt = 1; attempt <= maxRetries; attempt++) {
              try {
                profileData = await getProfileByUid(firebaseUser.uid);
                if (profileData) {
                  logger.info(`✅ Profile loaded successfully on attempt ${attempt}`);
                  break;
                } else {
                  logger.warn(`Profile returned null on attempt ${attempt}`);
                  // Wait before retry (increasing delay like original app)
                  if (attempt < maxRetries) {
                    await new Promise(resolve => setTimeout(resolve, attempt * 1000));
                  }
                }
              } catch (error) {
                profileLoadError = error;
                logger.warn(`Profile load attempt ${attempt} failed:`, error);
                if (attempt < maxRetries) {
                  await new Promise(resolve => setTimeout(resolve, attempt * 1000));
                }
              }
            }

            if (profileData) {
              // Successfully loaded profile
              const userData = convertToUser(firebaseUser, profileData);
              dispatch(login(userData));
              // Also set the current profile in userSlice (includes isAdmin from API)
              dispatch(setCurrentProfile(profileData));
              setUser(firebaseUser);
              setIsAuthenticated(true);

              // Initialize IAP module after login (matching original app pattern)
              try {
                await simpleIAPService.initialize(firebaseUser.uid);
                logger.info('✅ IAP initialized for user:', firebaseUser.uid);
                dispatch(attachPurchaseListener() as any);
              } catch (iapError) {
                logger.error('Failed to initialize IAP:', iapError);
                // Even if RevenueCat fails (e.g., on web), still check for admin-granted Peak from Firebase
                dispatch(checkSubscriptionStatus() as any);
              }

              // Initialize push notifications after login
              try {
                await initializePushNotificationsNative();
                // Also save any pending token that arrived before auth completed
                await savePendingPushToken(firebaseUser.uid);
                logger.info('✅ Push notifications initialized for user:', firebaseUser.uid);
              } catch (pushError) {
                logger.error('Failed to initialize push notifications:', pushError);
              }

              // Trigger background health sync (non-blocking)
              // This will safely exit early if Health is not enabled or not available
              healthSyncService.performBackgroundSync().catch(() => {});

              // Migrate belt data from profile to /beltPromotions/ if needed (runs in background)
              // This ensures all users have proper Firebase keys for their belt promotions
              getUserBeltPromotions(firebaseUser.uid).catch(err => {
                logger.warn('Belt promotion migration check failed:', err);
              });
            } else {
              // Profile load failed - log the error and use minimal fallback data
              // The user will be authenticated but may have incomplete profile data
              const errorMessage = profileLoadError
                ? `Profile load failed: ${profileLoadError}`
                : 'Profile not found in database';
              logger.error(`❌ ${errorMessage}`);

              const fallbackUserData = convertToUser(firebaseUser, {
                uid: firebaseUser.uid,
                email: firebaseUser.email || '',
                username: firebaseUser.displayName || `User${firebaseUser.uid.substring(0, 5)}`,
                firstName: '',
                lastName: ''
              } as any);

              // Login with fallback data - user can still access basic features
              // Admin diagnostics page can help identify and repair such profiles
              dispatch(login(fallbackUserData));
              // Set fallback profile in userSlice (won't have isAdmin since API call failed)
              dispatch(setCurrentProfile(fallbackUserData as any));
              setUser(firebaseUser);
              setIsAuthenticated(true);

              // Still initialize IAP even with fallback profile
              try {
                await simpleIAPService.initialize(firebaseUser.uid);
                dispatch(attachPurchaseListener() as any);
              } catch (iapError) {
                logger.error('Failed to initialize IAP (fallback):', iapError);
                // Even if RevenueCat fails (e.g., on web), still check for admin-granted Peak from Firebase
                dispatch(checkSubscriptionStatus() as any);
              }

              // Initialize push notifications with fallback profile
              try {
                await initializePushNotificationsNative();
                // Also save any pending token that arrived before auth completed
                await savePendingPushToken(firebaseUser.uid);
                logger.info('✅ Push notifications initialized (fallback profile)');
              } catch (pushError) {
                logger.error('Failed to initialize push notifications (fallback):', pushError);
              }

              // Trigger background health sync (non-blocking)
              // This will safely exit early if Health is not enabled or not available
              healthSyncService.performBackgroundSync().catch(() => {});

              // Migrate belt data from profile to /beltPromotions/ if needed (runs in background)
              getUserBeltPromotions(firebaseUser.uid).catch(err => {
                logger.warn('Belt promotion migration check failed (fallback):', err);
              });
            }
          } else {
            // User signed out
            dispatch(logout());
            setUser(null);
            setIsAuthenticated(false);

            // Reset IAP service on logout
            simpleIAPService.reset();

            // Cleanup push notifications on logout
            try {
              await cleanupPushNotificationsNative();
            } catch (pushError) {
              logger.error('Failed to cleanup push notifications:', pushError);
            }
          }

          // Mark as initialized after first auth check
          completeInitialization();
        });

        // Note: Firebase automatically restores auth state on app resume
        // No additional lifecycle listeners needed

      } catch (err: any) {
        logger.error('❌ Auth setup error:', err);
        setError(`Failed to setup auth: ${err.message}`);
        completeInitialization();
      }
    };

    initializeAuth();

    // Cleanup function
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [dispatch]);

  // Show loading screen while initializing
  if (!isInitialized) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-white dark:bg-gray-900">
        <div className="text-center">
          <img
            src="/img/marunetransparentlogo.png"
            alt="Marune Logo"
            className="w-64 h-auto mx-auto mb-8"
          />
          <h1 className="text-2xl font-semibold text-gray-700 dark:text-gray-200 mb-4">Loading...</h1>
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          {error && (
            <p className="text-red-500 mt-4">{error}</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <FirebaseContext.Provider
      value={{
        isAuthenticated,
        isInitialized,
        isLoading,
        user,
        error,
        networkStatus: {},
        checkFirebaseConnectivity: async () => true,
        checkConnectivity: async () => true,
        resetFlags: () => {}
      }}
    >
      {children}
    </FirebaseContext.Provider>
  );
};

export default FirebaseProvider;